import React from "react"
import { Box } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { dsColorNew } from "@palette"
import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"
import { readPeriod } from "Pages/DsRepricing/Page/@components/periodShape"
import { HourField, Label } from "Pages/DsRepricing/Page/@components/WhenItRuns"
import { factoryCadence, pickupDatesLine } from "Pages/DsRepricing/Page/@services/cadence"
import { useDraftRateShops } from "Pages/DsRepricing/Page/@hooks/useRateShops"

// The foot of "When does it apply?" (CU-869fkr1a9; Curro, 2026-10-21):
// the pickup dates the engine searches, said back from the period with the
// engine's count and ranges (UX Obs. 8-10, 14), and the pickup / dropoff
// time of the rentals searched. The times moved here from "When does it
// run", which is only the scheduler now; they still live in the cadence
// (`whenItRuns.pickup_hour / dropoff_hour`) — a move of the field, not of
// the data.

/**
 * Read only: the lead times or seasons of the card, the engine's count and
 * ranges when it has them. Amber "No pickup dates" when the period has
 * neither: such a strategy cannot be switched on (criterion 17).
 */
function PickupDatesLine({ period, estimate }) {
	const line = pickupDatesLine(period, estimate)
	const leads = (period?.leadTimes || []).map(lt => Number(lt?.leadTo)).filter(Number.isFinite)
	return (
		<Box>
			<Label>Pickup dates searched</Label>
			{line ? (
				<Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.25, borderRadius: '10px', bgcolor: dsColorNew.tableRowAlt, border: `1px solid ${dsColorNew.grayBorder}` }}>
					<IconSeeker icon="circle-info" sx={{ color: dsColorNew.appBlue, fontSize: 14, mt: '3px', flex: 'none' }}/>
					<Box sx={{ flex: 1, fontSize: 13, color: dsColorNew.textDark, lineHeight: 1.5 }}>
						The engine searches the pickup dates above: {line}.
						<Box component="span" sx={{ display: 'block', fontSize: 12, color: dsColorNew.textMuted }}>
							{leads.length
								? `Counted from today. The farthest lead time (${Math.max(...leads)} days) is how far ahead the engine looks.`
								: 'Counted from today, up to the end of the last season.'}
						</Box>
					</Box>
				</Box>
			) : (
				<Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.25, borderRadius: '10px', bgcolor: dsColorNew.noteAmberBg, border: `1px solid ${dsColorNew.noteAmberBorder}` }}>
					<IconSeeker icon="triangle-exclamation" sx={{ color: dsColorNew.noteAmber, fontSize: 14, mt: '3px', flex: 'none' }}/>
					<Box sx={{ flex: 1, fontSize: 13, color: dsColorNew.noteAmber, lineHeight: 1.5 }}>
						<strong>No pickup dates.</strong> This strategy has no lead times and no seasons, so the engine has nothing to search.
						Set one of the two above. Until then it cannot be switched on.
					</Box>
				</Box>
			)}
		</Box>
	)
}

export default function DsRepricingPickupDates() {
	const data = dsRepricingDrawerData.value || {}
	const cadence = data.whenItRuns || factoryCadence()
	const shop = useDraftRateShops()
	// Editing a time makes the cadence the strategy's own, as any cadence field (D19).
	const set = (patch) => {
		const current = dsRepricingDrawerData.value || {}
		dsRepricingDrawerData.value = { ...current, whenItRuns: { ...(current.whenItRuns || factoryCadence()), ...patch, reviewed: true } }
	}
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
			<PickupDatesLine period={readPeriod(data)} estimate={shop.estimate}/>
			<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
				<HourField label="Pickup time" value={cadence.pickupHour} onChange={v => set({ pickupHour: v })}/>
				<HourField label="Dropoff time" value={cadence.dropoffHour} onChange={v => set({ dropoffHour: v })}/>
			</Box>
		</Box>
	)
}
